"use client";
/* eslint-disable @typescript-eslint/no-explicit-any */

import { motion } from "framer-motion";
import { jwtDecode } from "jwt-decode";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import phone from "phone";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { BiSend } from "react-icons/bi";
import { FaTimes } from "react-icons/fa";
import { PhoneInput } from "react-international-phone";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

import { useWindowSize } from "@/hooks/useWindowSize.hook";
import { localeToCountry } from "@/utils/locale";
import { validateEmail } from "@/utils/string";
import { Button } from "../common/button";
import { Input } from "../common/input";
import { Select } from "../common/select";

interface IFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  position: string;
}

const ChatFloat: React.FC = () => {
  const t = useTranslations("ChatFloatComponentLayout");
  const tg = useTranslations("global");
  const locale = useLocale();
  const { width, height } = useWindowSize();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [unreadMessage, setUnreadMessage] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [user, setUser] = useState<{
    name?: string;
    email?: string;
    phone?: string;
  }>({});
  const [formData, setFormData] = useState<IFormData>({
    name: "",
    email: "",
    phone: "",
    company: "",
    position: "",
  });

  const positions = t.raw("form.positions") as {
    value: string;
    label: string;
  }[];

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);

  const isMobile = width < 425;

  // Inicializa dados e mensagens do `localStorage`
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | KeyboardEvent) => {
      if (!chatRef.current?.contains(event.target as Node)) setIsOpen(false);
      if ((event as KeyboardEvent)?.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("keydown", handleClickOutside);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleClickOutside);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      const storedMessages = localStorage.getItem("@chatMessages");
      const storedMessagesNormalize = storedMessages
        ? JSON.parse(storedMessages)
        : [];
      setMessages(storedMessagesNormalize);
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedMessages = localStorage.getItem("@chatMessages");
      const storedMessagesNormalize = storedMessages
        ? JSON.parse(storedMessages)
        : [];
      const storedChatData = localStorage.getItem("@formData");
      setMessages(storedMessagesNormalize);
      if (!storedMessagesNormalize.length)
        setTimeout(() => {
          setMessages([
            {
              sender: "agent",
              text: t('welcome_message'),
              read: false,
            },
          ]);
        }, 10_000);
      setUser(storedChatData ? JSON.parse(storedChatData) : null);
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem("@chatMessages", JSON.stringify(messages));
    }
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isInitialized]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      chatRef.current?.focus();
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    let userInteracted = false;

    const enableAudio = () => {
      userInteracted = true;
      document.removeEventListener("click", enableAudio);
      document.removeEventListener("keydown", enableAudio);
    };

    document.addEventListener("click", enableAudio);
    document.addEventListener("keydown", enableAudio);

    if (!isOpen && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (
        lastMessage.sender === "agent" &&
        !lastMessage.read &&
        userInteracted
      ) {
        const audio = new Audio("/audio/notification.wav");
        audio.volume = 0.1;
        audio.play().catch((err) => {
          console.error("Error playing audio:", err);
        });
      }
    }

    return () => {
      document.removeEventListener("click", enableAudio);
      document.removeEventListener("keydown", enableAudio);
    };
  }, [messages, isOpen, isInitialized]);

  const toggleChat = () => {
    const storedChatData = localStorage.getItem("@formData");

    if (storedChatData) setUser(JSON.parse(storedChatData));

    setIsOpen(!isOpen);
    setUnreadMessage(false);
    setMessages(messages.map((m: any) => ({ ...m, read: true })));
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newMessage.trim()) {
      const userMessage = { sender: "user", text: newMessage };
      setMessages([...messages, userMessage]);
      setNewMessage("");
      setIsWaitingForResponse(true);

      const token = localStorage.getItem("@token");

      if (!token) throw new Error("Token is missing.");

      try {
        const decodedToken: { chatId?: string } = jwtDecode(token);
        const chatId = decodedToken.chatId;

        if (!chatId) {
          throw new Error("Chat ID is missing in the token.");
        }

        const response = await fetch("/api/message", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            content: {
              text: newMessage,
            },
            chatId,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to send message");
        }

        const { result } = await response.json();
        setMessages((prev) => [
          ...prev,
          { sender: "agent", text: result.response },
        ]);
      } catch (error) {
        console.error("Error sending message:", error);
        setMessages((prev) => [
          ...prev,
          {
            sender: "agent",
            text: t("error_send_message"),
          },
        ]);
      } finally {
        setIsWaitingForResponse(false);
      }
    }
  };

  const getLastMessagePreview = () => {
    if (messages.length === 0) return "StaryaAI";
    const lastMessage = messages[messages.length - 1].text;
    return lastMessage.length > 20
      ? `${lastMessage.slice(0, 20)}...`
      : lastMessage;
  };

  const handleChatDataSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) return;

    setIsSubmiting(true);

    try {
      const response = await fetch("/api/lead", {
        method: "POST",
        body: JSON.stringify({ ...formData, locale }),
      });

      if (!response.ok) {
        throw new Error("Failed to send the request");
      }

      toast.success(t("form.success_message"));

      const { result } = await response.json();

      localStorage.setItem("@formData", JSON.stringify(formData));
      localStorage.setItem("@token", result.jwt);

      setUser(formData);

      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        position: "",
      });
    } catch (error) {
      console.error(error);
      toast.error(t("form.error_message"));
    } finally {
      setIsSubmiting(false);
    }
  };

  const handleChange = (key: string, value: unknown) => {
    setFormData({ ...formData, [key]: value });
  };

  const isFormValid =
    Object.values(formData).every((value) => value !== "") &&
    validateEmail(formData.email) &&
    phone(formData.phone).isValid;

  return (
    <motion.div
      ref={chatRef}
      className={`fixed ${isOpen ? "z-50" : "z-10"} bottom-4 -right-52 bg-white shadow-lg ${unreadMessage ? "animate-bounce" : ""}`}
      animate={{
        width: isOpen ? (isMobile ? width : 350) : 208,
        height: isOpen ? (isMobile ? height : 500) : 64,
        borderRadius: isOpen ? 16 : 32,
        right: isInitialized ? (isMobile ? 0 : 16) : -1000,
        bottom: isMobile && isOpen ? 0 : undefined,
      }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
      style={{
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: isOpen ? "space-between" : "center",
        alignItems: isOpen ? "stretch" : "center",
      }}
    >
      <motion.div
        className="flex items-center justify-between p-4"
        initial={false}
        animate={{
          height: isOpen ? "auto" : "100%",
          opacity: isOpen ? 1 : 0,
        }}
        transition={{ duration: 0.5 }}
        style={{ display: isOpen ? "flex" : "none" }}
      >
        <div className="flex items-center">
          <div className="relative mr-2">
            <div className="absolute w-2 h-2 bg-green-500 rounded-full bottom-2 right-2"></div>
            <div className="absolute w-2 h-2 bg-green-500 rounded-full bottom-2 right-2 animate-ping"></div>
            <Image
              src="/agents/julia_circle_2.webp"
              alt="Agent Julia"
              width={64}
              height={64}
              className="rounded-full border-4 border-white"
            />
          </div>
          <h2 className="ml-4 text-lg font-semibold">{t("title")}</h2>
        </div>
        <button
          className="text-gray-500 hover:text-gray-700 transition"
          onClick={toggleChat}
        >
          <FaTimes size={20} />
        </button>
      </motion.div>
      {/* User Data Form */}
      {!user ? (
        <motion.form
          onSubmit={handleChatDataSubmit}
          className="flex flex-col gap-4 h-full justify-between px-4 pb-4"
          initial={false}
          animate={{
            opacity: isOpen ? 1 : 0,
          }}
          transition={{ duration: 0.5 }}
          style={{
            display: isOpen ? "flex" : "none",
          }}
        >
          <div className="flex flex-col gap-4 py-4">
            <Input
              id="julia_name"
              label={t("form.name")}
              type="text"
              value={formData.name}
              onChange={(v) => handleChange("name", v.target.value)}
              required
              aria-required="true"
              aria-invalid={!formData.name ? "true" : "false"}
            />

            {/* E-mail */}
            <Input
              id="julia_email"
              label={t("form.email")}
              type="email"
              value={formData.email}
              onChange={(v) => handleChange("email", v.target.value)}
              required
              aria-required="true"
              aria-invalid={!formData.email ? "true" : "false"}
            />

            {/* Telefone */}
            <PhoneInput
              defaultCountry={localeToCountry[locale] || locale}
              value={formData.phone}
              onChange={(v) => handleChange("phone", v)}
              inputClassName="w-full"
            />

            {/* Empresa */}
            <Input
              id="julia_company"
              label={t("form.company")}
              type="text"
              value={formData.company}
              onChange={(v) => handleChange("company", v.target.value)}
              required
              aria-required="true"
              aria-invalid={!formData.company ? "true" : "false"}
            />

            <Select
              id="contact_position"
              label={t("form.position")}
              options={positions}
              value={formData.position}
              onChange={(v) => handleChange("position", v.target.value)}
              required
              aria-required="true"
              aria-invalid={!formData.position ? "true" : "false"}
            />
          </div>
          <Button
            type="submit"
            label={isSubmiting ? tg("loading") : t("form.submit")}
            className="w-full py-3 font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition"
            disabled={!isFormValid || isSubmiting}
          />
        </motion.form>
      ) : (
        <>
          <motion.div
            className="flex-1 overflow-y-auto px-4 scrollbar-thin scrollbar-transparent"
            initial={false}
            animate={{ opacity: isOpen ? 1 : 0 }}
            transition={{ duration: 0.5 }}
            style={{ display: isOpen ? "block" : "none" }}
          >
            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`mb-2 flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`p-2 rounded-lg text-sm ${message.sender === "user" ? "bg-gray-200/80 text-right" : "bg-slate-50 text-left"} max-w-xs`}
                >
                  {message.sender === "agent" ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkMath]}
                      rehypePlugins={[rehypeRaw]}
                      className="prose text-sm"
                    >
                      {message.text.replaceAll(/\$/gm, "\\$")}
                    </ReactMarkdown>
                  ) : (
                    message.text
                  )}
                </div>
              </motion.div>
            ))}
            {isWaitingForResponse && (
              <motion.div
                className="flex justify-start items-center mb-2 rounded-lg bg-slate-50 text-left"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center space-x-1 mx-2 p-2">
                  <div className="h-2 w-2 bg-gray-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="h-2 w-2 bg-gray-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="h-2 w-2 bg-gray-500 rounded-full animate-bounce "></div>
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </motion.div>

          {/* Input Section */}
          <motion.form
            onSubmit={handleSendMessage}
            className="flex items-center p-4 border-t"
            initial={false}
            animate={{
              opacity: isOpen ? 1 : 0,
            }}
            transition={{ duration: 0.5 }}
            style={{
              display: isOpen ? "flex" : "none",
            }}
          >
            <input
              type="text"
              placeholder={t("send_message")}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              className="flex-1 border border-gray-300 rounded-l-lg p-2 focus:outline-none h-full text-sm"
              disabled={isWaitingForResponse}
            />
            <Button
              type="submit"
              label={<BiSend size={20} />}
              aria-label={t("send_message")}
              className="flex-shrink-0 py-2 px-3 rounded-l-none rounded-r-md transition-colors duration-300"
              disabled={isWaitingForResponse}
            />
          </motion.form>
        </>
      )}

      {/* Floating Button (when closed) */}
      {!isOpen && (
        <motion.div
          onClick={toggleChat}
          className="cursor-pointer flex items-center w-52 h-16 bg-white rounded-full shadow-lg hover:shadow-2xl transition"
        >
          <div className="relative mr-2">
            <div className="absolute w-2 h-2 bg-green-500 rounded-full bottom-2 right-2"></div>
            <div className="absolute w-2 h-2 bg-green-500 rounded-full bottom-2 right-2 animate-ping"></div>
            <Image
              src="/agents/julia_circle_2.webp"
              alt="Agent Julia"
              width={64}
              height={64}
              className="rounded-full border-4 border-white"
            />
          </div>
          <div className="flex flex-col text-left">
            <p className="font-semibold">{t("title")}</p>
            <p className="text-gray-500 text-xs">{getLastMessagePreview()}</p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ChatFloat;
